import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  User,
  ExtractedData,
  UsageData,
  DashboardStats,
  AuditLog,
  BillingInfo,
  Invoice,
  SignUpRequest,
  SignUpResponse,
  BillingTransaction,
} from "../types/api";

// Configuration for the API service
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.snaplading.com/api";

// Debug logging for API configuration
console.log("API Service Configuration:", {
  baseURL: API_BASE_URL,
  envVar: import.meta.env.VITE_API_URL,
  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
});

// Global callback for Auth0 token access
let getAuth0TokenCallback: (() => Promise<string | null>) | null = null;

// Request deduplication cache
const pendingRequests = new Map<string, Promise<any>>();

class ApiService {
  private client: AxiosInstance;
  private auth0TokenCallback?: () => Promise<string | null>;
  private tokenRefreshCallback?: () => Promise<string | null>;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Enable cookies for authentication
    });

    // Request interceptor for adding Auth0 tokens and dev role headers
    this.client.interceptors.request.use(
      async (config) => {
        try {
          console.log("[API] Request interceptor - URL:", config.url);

          // Add Auth0 token if available
          if (getAuth0TokenCallback) {
            console.log("[API] Getting Auth0 token...");
            const token = await getAuth0TokenCallback();
            if (token) {
              config.headers["Authorization"] = `Bearer ${token}`;
              console.log("[API] Auth0 token added to request");
            } else {
              console.warn("[API] No Auth0 token available");
            }
          } else {
            console.warn("[API] No Auth0 token callback available");
          }

          // Add dev role for development mode
          const devRole = localStorage.getItem("devRole");
          if (devRole) {
            config.headers["X-Dev-Role"] = devRole;
            console.log("[API] Dev role added:", devRole);
          }
        } catch (error) {
          console.error("[API] Error in request interceptor:", error);
        }

        return config;
      },
      (error) => {
        console.error("[API] Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        console.log("[API] Response success:", {
          url: response.config.url,
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data,
          dataPreview:
            typeof response.data === "string"
              ? response.data.substring(0, 200)
              : "Object",
        });
        return response;
      },
      async (error) => {
        console.error("[API] Response error:", {
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
        });

        const originalRequest = error.config;

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          console.warn("[API] 401 error - authentication failed");

          // Clear any local storage and redirect to landing page
          localStorage.removeItem("devRole");

          // For SPA, don't do hard redirect, let the app handle it
          console.warn(
            "[API] Authentication failed, but letting app handle redirect"
          );
        }

        return Promise.reject(error);
      }
    );
  }

  // Method to set the Auth0 token callback
  setAuth0TokenCallback(callback: () => Promise<string | null>) {
    getAuth0TokenCallback = callback;
  }

  // Legacy method for compatibility
  setTokenRefreshCallback(callback: () => Promise<string | null>) {
    this.setAuth0TokenCallback(callback);
  }

  // Request deduplication helper
  private async deduplicateRequest<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if there's already a pending request
    if (pendingRequests.has(key)) {
      console.log(`[API] Deduplicating request: ${key}`);
      return await pendingRequests.get(key)!;
    }

    // Create new request
    const requestPromise = requestFn();
    pendingRequests.set(key, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the pending request
      pendingRequests.delete(key);
    }
  }

  // Generic HTTP methods
  async get(url: string, config?: any) {
    const response = await this.client.get(url, config);
    return response;
  }

  async post(url: string, data?: any, config?: any) {
    const response = await this.client.post(url, data, config);
    return response;
  }

  async put(url: string, data?: any, config?: any) {
    const response = await this.client.put(url, data, config);
    return response;
  }

  async delete(url: string, config?: any) {
    const response = await this.client.delete(url, config);
    return response;
  }

  // Health check
  async checkHealth() {
    const response = await this.client.get("/health");
    return response.data;
  }

  // Auth endpoints
  async login(
    credentials: LoginRequest,
    devRole?: "admin" | "user"
  ): Promise<ApiResponse<LoginResponse>> {
    const headers: Record<string, string> = {};
    if (devRole) {
      headers["X-Dev-Role"] = devRole;
    }
    const response = await this.client.post("/auth/login", credentials, {
      headers,
    });
    return response.data;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.client.post("/auth/logout");
    return response.data;
  }

  async validateToken(): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.get("/auth/validate");
    return response.data;
  }

  // Processing endpoints
  async getJobStatus(jobId: string): Promise<any> {
    const response = await this.client.get("/processing/jobs", {
      params: { jobId },
    });
    return response.data;
  }

  async cancelJob(jobId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/processing/job/${jobId}/cancel`);
    return response.data;
  }

  async updateExtractedData(
    jobId: string,
    data: ExtractedData
  ): Promise<ApiResponse<void>> {
    const response = await this.client.put(
      `/processing/job/${jobId}/data`,
      data
    );
    return response.data;
  }

  async generateEDI(
    jobId: string,
    documentType: string
  ): Promise<ApiResponse<{ ediDocument: string }>> {
    const response = await this.client.post(
      `/processing/job/${jobId}/generate-edi`,
      { documentType }
    );
    return response.data;
  }

  // Admin endpoints
  async getTenants(): Promise<ApiResponse<any[]>> {
    const response = await this.client.get("/admin/tenants");
    return response.data;
  }

  async getUsageReport(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<UsageData[]>> {
    const response = await this.client.get("/admin/usage", { params });
    return response.data;
  }

  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.client.get("/admin/stats");
    return response.data;
  }

  async getAuditLogs(params: {
    page: number;
    pageSize: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ logs: AuditLog[]; total: number }>> {
    const response = await this.client.get("/admin/audit-logs", { params });
    return response.data;
  }

  // Billing endpoints
  async getCurrentUsage(): Promise<ApiResponse<BillingInfo>> {
    const response = await this.client.get("/billing/current-usage");
    return response.data;
  }

  async getInvoices(): Promise<ApiResponse<Invoice[]>> {
    const response = await this.client.get("/billing/invoices");
    return response.data;
  }

  async getSubscription(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/billing/subscription");
    return response.data;
  }

  // Upload endpoints
  async processImages(
    files: File[],
    ediSenderId?: string,
    ediReceiverId?: string
  ): Promise<any> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("Files", file);
    });
    if (ediSenderId) formData.append("EdiSenderId", ediSenderId);
    if (ediReceiverId) formData.append("EdiReceiverId", ediReceiverId);

    const response = await this.client.post(
      "/upload/process-images",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async reprocessJob(jobId: string): Promise<any> {
    const response = await this.client.post(
      `/processing/job/${jobId}/reprocess`
    );
    return response.data;
  }

  async getJobs(params?: {
    page?: number;
    pageSize?: number;
    jobId?: string;
  }): Promise<any> {
    const response = await this.client.get("/processing/jobs", { params });
    return response.data;
  }

  // User endpoints
  async getUserSettings(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/admin/user/settings");
    return response.data;
  }

  async updateUserSettings(settings: any): Promise<ApiResponse<any>> {
    const response = await this.client.put("/admin/user/settings", settings);
    return response.data;
  }

  async getReviewQueue(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/review/list");
    return response.data;
  }

  async claimReviewJob(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/review/claim/${jobId}`);
    return response.data;
  }

  async unclaimReviewJob(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/review/unclaim/${jobId}`);
    return response.data;
  }

  async getReviewJob(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/review/job/${jobId}`);
    return response.data;
  }

  async acceptReviewJob(
    jobId: string,
    fieldEdits: Record<string, string>
  ): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/review/accept/${jobId}`, {
      fieldEdits,
    });
    return response.data;
  }

  async reprocessReviewJob(
    jobId: string,
    instructions: string
  ): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/review/reprocess/${jobId}`, {
      instructions,
    });
    return response.data;
  }

  // Saved interchange IDs endpoints
  async getSavedInterchangeIds(): Promise<
    ApiResponse<{
      savedSenderIds: Array<{ label: string; interchangeId: string }>;
      savedReceiverIds: Array<{ label: string; interchangeId: string }>;
    }>
  > {
    const response = await this.client.get("/admin/user/interchange-ids");
    return response.data;
  }

  async addSavedSenderId(
    label: string,
    interchangeId: string
  ): Promise<ApiResponse<Array<{ label: string; interchangeId: string }>>> {
    const response = await this.client.post("/admin/user/sender-ids", {
      Label: label,
      InterchangeId: interchangeId,
    });
    return response.data;
  }

  async deleteSavedSenderId(
    interchangeId: string
  ): Promise<ApiResponse<Array<{ label: string; interchangeId: string }>>> {
    const response = await this.client.delete(
      `/admin/user/sender-ids/${encodeURIComponent(interchangeId)}`
    );
    return response.data;
  }

  async addSavedReceiverId(
    label: string,
    interchangeId: string
  ): Promise<ApiResponse<Array<{ label: string; interchangeId: string }>>> {
    const response = await this.client.post("/admin/user/receiver-ids", {
      Label: label,
      InterchangeId: interchangeId,
    });
    return response.data;
  }

  async deleteSavedReceiverId(
    interchangeId: string
  ): Promise<ApiResponse<Array<{ label: string; interchangeId: string }>>> {
    const response = await this.client.delete(
      `/admin/user/receiver-ids/${encodeURIComponent(interchangeId)}`
    );
    return response.data;
  }

  async initializeUser(): Promise<ApiResponse<any>> {
    return this.deduplicateRequest("initializeUser", async () => {
      const response = await this.client.post("/user/initialize");
      return response.data;
    });
  }

  async isUserInitialized(): Promise<
    ApiResponse<{ initialized: boolean; initializedAt?: string }>
  > {
    return this.deduplicateRequest("isUserInitialized", async () => {
      const response = await this.client.get("/user/is-initialized");
      return response.data;
    });
  }

  async signup(request: SignUpRequest): Promise<ApiResponse<SignUpResponse>> {
    const response = await this.client.post("/auth/signup", request);
    return response.data;
  }

  async testAuth0(): Promise<any> {
    const response = await this.client.get("/auth/test");
    return response.data;
  }

  // Debug endpoints
  async debugAuthContext(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/admin/debug/auth-context");
    return response.data;
  }

  // Billing endpoints
  async createCheckoutSession(
    amount: number
  ): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    const response = await this.client.post(
      "/billing/create-checkout-session",
      {
        amount,
      }
    );
    return response.data;
  }

  async getBillingInfo(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/billing/info");
    return response.data;
  }

  async getBillingBalance(): Promise<ApiResponse<number>> {
    const response = await this.client.get("/billing/balance");
    return response.data;
  }

  async getBillingTransactions(
    limit: number = 50,
    skip: number = 0
  ): Promise<ApiResponse<BillingTransaction[]>> {
    const response = await this.client.get("/billing/transactions", {
      params: { limit, skip },
    });
    return response.data;
  }

  async getPricingPlan(): Promise<ApiResponse<string>> {
    const response = await this.client.get("/billing/pricing-plan");
    return response.data;
  }

  async updatePricingPlan(pricingPlan: string): Promise<ApiResponse<string>> {
    const response = await this.client.put("/billing/pricing-plan", {
      pricingPlan,
    });
    return response.data;
  }

  // SFTP endpoints
  async getSftpCredentials(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/sftp/credentials");
    return response.data;
  }

  // API Keys
  async listApiKeys(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/apikeys");
    return response.data;
  }

  async createApiKey(
    name: string,
    description?: string
  ): Promise<ApiResponse<{ id: string; apiKey: string; keyPrefix: string }>> {
    const response = await this.client.post("/apikeys", { name, description });
    return response.data;
  }

  async revokeApiKey(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/apikeys/${id}`);
    return response.data;
  }

  async rotateApiKey(
    id: string
  ): Promise<ApiResponse<{ id: string; apiKey: string; keyPrefix: string }>> {
    const response = await this.client.post(`/apikeys/${id}/rotate`);
    return response.data;
  }

  async createSftpUser(
    userEmail: string,
    customPassword?: string
  ): Promise<ApiResponse<any>> {
    const response = await this.client.post("/sftp/create-user", {
      userEmail,
      customPassword,
    });
    return response.data;
  }

  async resetSftpPassword(
    userEmail: string,
    newPassword?: string
  ): Promise<ApiResponse<any>> {
    const response = await this.client.post("/sftp/reset-password", {
      userEmail,
      newPassword,
    });
    return response.data;
  }

  async getSftpUserCreationStatus(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/sftp/user-creation-status");
    return response.data;
  }

  async testSftpConnection(): Promise<ApiResponse<any>> {
    const response = await this.client.get("/sftp/test-connection");
    return response.data;
  }

  // Admin SFTP endpoints
  async getAllSftpUsers(): Promise<ApiResponse<any[]>> {
    const response = await this.client.get("/sftp/admin/users");
    return response.data;
  }

  async deleteSftpUser(username: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/sftp/admin/users/${username}`);
    return response.data;
  }

  async getSftpCreationJobs(tenantId?: string): Promise<ApiResponse<any[]>> {
    const response = await this.client.get("/sftp/admin/creation-jobs", {
      params: { tenantId },
    });
    return response.data;
  }

  async adminResetSftpPassword(
    tenantId: string,
    userEmail: string,
    newPassword?: string
  ): Promise<ApiResponse<any>> {
    const response = await this.client.post("/sftp/admin/reset-password", {
      tenantId,
      userEmail,
      newPassword,
    });
    return response.data;
  }
}

export const apiService = new ApiService();
export const api = apiService; // Named export for compatibility
export default apiService;
